import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Buildings, Plus, ArrowSquareOut, Warning, CurrencyDollar, Envelope, CaretRight } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import useAuthStore from '../store/authStore';
import PricingEditor from './superadmin/PricingEditor';
import TenantDetailDrawer from './superadmin/TenantDetailDrawer';
import MarketingLeadsInbox from './superadmin/MarketingLeadsInbox';
import EnvironmentBadge from '../components/EnvironmentBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Super-admin only page — the ONE place a Vooxy operator can:
 *   - See every tenant on the platform
 *   - Onboard a new tenant
 *   - Edit the public /pricing page (plans + features matrix)
 */
const SuperAdmin = () => {
  const { user } = useAuthStore();
  const isSuperAdmin = user?.is_super_admin === true || user?.email === 'admin@vooxy.com';
  const [tab, setTab] = useState('tenants');

  if (!isSuperAdmin) {
    return (
      <div className="p-6" data-testid="super-admin-forbidden">
        <div className="medical-card p-8 text-center">
          <Warning size={48} className="text-[#EF4444] mx-auto mb-3" weight="duotone" />
          <h2 className="text-xl font-bold text-[#052A23] mb-1" style={{ fontFamily: 'Outfit' }}>Restricted</h2>
          <p className="text-sm text-[#6D8E87]">The tenant admin console is only available to the Vooxy platform super-admin.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Full-width environment banner. Renders nothing in production (kept
          clean), amber in preview, grey in local dev. Sits above the padded
          content so it spans edge-to-edge. */}
      <EnvironmentBadge variant="banner" />

      <div className="p-6 space-y-6" data-testid="super-admin-page">
        <div className="flex items-center gap-3 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-widest text-emerald-600 font-bold" data-testid="super-admin-brand-label">Vooxy Platform</p>
            <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>Admin Console</h1>
          </div>
          <EnvironmentBadge variant="pill" />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-[#D5E8E4]">
          <TabBtn active={tab === 'tenants'} onClick={() => setTab('tenants')} icon={Buildings} label="Tenants" testid="tab-tenants" />
          <TabBtn active={tab === 'leads'} onClick={() => setTab('leads')} icon={Envelope} label="Marketing Leads" testid="tab-leads" />
          <TabBtn active={tab === 'pricing'} onClick={() => setTab('pricing')} icon={CurrencyDollar} label="Pricing" testid="tab-pricing" />
        </div>

        {tab === 'tenants' && <TenantsTab />}
        {tab === 'leads' && <MarketingLeadsInbox />}
        {tab === 'pricing' && <PricingEditor />}
      </div>
    </>
  );
};

const TabBtn = ({ active, onClick, icon: Icon, label, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`inline-flex items-center gap-2 px-4 py-2.5 -mb-px border-b-2 text-sm font-semibold transition ${
      active
        ? 'border-[#00a69c] text-[#00a69c]'
        : 'border-transparent text-[#6D8E87] hover:text-[#052A23]'
    }`}
  >
    <Icon size={16} weight={active ? 'fill' : 'regular'} />
    {label}
  </button>
);

/* ────────────────────────────── Tenants tab ────────────────────────────── */
const TenantsTab = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [drawerSlug, setDrawerSlug] = useState(null);
  // Live plan list — sourced from the same collection the drawer + public
  // /pricing page use, so if the founder renames Standard → "Starter Plus"
  // in the Pricing Editor, the Create Tenant modal reflects it immediately.
  const [availablePlans, setAvailablePlans] = useState([]);
  const [form, setForm] = useState({
    subdomain: '', company_name: '', plan: '',
    admin_email: '', admin_name: '', admin_password: '',
    copy_catalog: false, copy_from: 'nilkamal',
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [justCreated, setJustCreated] = useState(null);
  const [catalogCount, setCatalogCount] = useState(null);
  const [catalogCountLoading, setCatalogCountLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/tenants`)
      .then(r => setTenants(r.data.tenants || []))
      .catch(e => setError(e.response?.data?.detail || 'Failed to load tenants'))
      .finally(() => setLoading(false));

    // Populate the plan dropdown from the live seeded plans collection so we
    // NEVER offer a plan value the backend can't resolve. Default the form to
    // the lowest tier (typically Standard) — matches what most new tenants pick.
    axios.get(`${API}/admin/plans`)
      .then(r => {
        const sorted = (r.data.plans || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        setAvailablePlans(sorted);
        if (sorted.length && !form.plan) {
          setForm(f => ({ ...f, plan: sorted[0].id }));
        }
      })
      .catch(() => { /* non-fatal — modal will just show an empty dropdown */ });
  }, []);

  useEffect(() => {
    if (!form.copy_catalog || !form.copy_from) { setCatalogCount(null); return; }
    setCatalogCountLoading(true);
    axios.get(`${API}/admin/tenants/${encodeURIComponent(form.copy_from)}/catalog-count`)
      .then(r => setCatalogCount(r.data.count))
      .catch(() => setCatalogCount(null))
      .finally(() => setCatalogCountLoading(false));
  }, [form.copy_catalog, form.copy_from]);

  const submitCreate = async () => {
    setCreating(true); setCreateError(null); setJustCreated(null);
    try {
      const payload = {
        subdomain: form.subdomain.trim().toLowerCase(),
        company_name: form.company_name.trim(),
        plan: form.plan || (availablePlans[0]?.id || 'standard'),
      };
      if (form.admin_email && form.admin_password && form.admin_name) {
        payload.admin_email = form.admin_email.trim().toLowerCase();
        payload.admin_password = form.admin_password;
        payload.admin_name = form.admin_name.trim();
      }
      if (form.copy_catalog && form.copy_from) {
        payload.copy_products_from = form.copy_from.trim().toLowerCase();
      }
      const r = await axios.post(`${API}/admin/tenants`, payload);
      setTenants(prev => [r.data, ...prev]);
      setJustCreated(r.data);
      setForm({
        subdomain: '', company_name: '', plan: availablePlans[0]?.id || '',
        admin_email: '', admin_name: '', admin_password: '',
        copy_catalog: false, copy_from: 'nilkamal',
      });
    } catch (e) {
      setCreateError(e.response?.data?.detail || 'Failed to create tenant');
    } finally { setCreating(false); }
  };

  if (loading) return <div className="p-6 text-[#00a69c]">Loading tenants…</div>;

  return (
    <div className="space-y-4" data-testid="tenants-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[#6D8E87]">{tenants.length} tenant{tenants.length === 1 ? '' : 's'} on the platform</p>
        <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-2" data-testid="create-tenant-button">
          <Plus size={18} weight="bold" /> New Tenant
        </button>
      </div>

      {error && (
        <div className="medical-card p-4 border-l-4 border-red-500 text-sm text-red-700">{error}</div>
      )}

      {justCreated && (
        <div className="medical-card p-4 border-l-4 border-[#00a69c] bg-[#F4F9F7]" data-testid="tenant-just-created">
          <p className="text-sm font-semibold text-[#052A23]">✅ Tenant &lsquo;{justCreated.company_name}&rsquo; created.</p>
          {justCreated.admin_created && (
            <p className="text-xs text-[#3B5A54] mt-1">
              Admin seeded: <b>{justCreated.admin_created.email}</b> (Role: President). They can log in at
              <b> /t/{justCreated.subdomain}/login</b>.
            </p>
          )}
          {justCreated.copied_products > 0 && (
            <p className="text-xs text-[#3B5A54] mt-1" data-testid="tenant-copied-products">
              📦 Seeded <b>{justCreated.copied_products}</b> products into their catalog (COGS zeroed).
            </p>
          )}
        </div>
      )}

      <div className="medical-card overflow-x-auto">
        <table className="w-full min-w-[700px]">
          <thead className="bg-[#F4F9F7]">
            <tr>
              <th className="text-left px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Subdomain</th>
              <th className="text-left px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Company</th>
              <th className="text-left px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Plan</th>
              <th className="text-left px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Status</th>
              <th className="text-left px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Created</th>
              <th className="text-left px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Open</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map(t => (
              <tr
                key={t.id}
                className="border-t border-[#D5E8E4] hover:bg-[#F4F9F7] cursor-pointer transition"
                onClick={() => setDrawerSlug(t.subdomain)}
                data-testid={`tenant-row-${t.subdomain}`}
              >
                <td className="px-6 py-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Buildings size={18} className="text-[#00a69c]" weight="duotone" />
                    <code className="text-[#052A23] font-semibold">{t.subdomain}</code>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm font-medium text-[#052A23]">{t.company_name}</td>
                <td className="px-6 py-4 text-xs uppercase tracking-wide text-[#6D8E87]">{t.plan}</td>
                <td className="px-6 py-4">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                    t.status === 'active' ? 'bg-[#D1FAE5] text-[#065F46]' : 'bg-red-100 text-red-700'
                  }`}>{t.status}</span>
                </td>
                <td className="px-6 py-4 text-xs text-[#6D8E87]">{t.created_at?.slice(0, 10)}</td>
                <td className="px-6 py-4">
                  {/* Path-based multi-tenancy — link into the tenant's login page.
                      Opens in a new tab so the super-admin doesn't lose their
                      console session. Login there uses the tenant's own user
                      accounts; the super-admin JWT is NOT auto-carried.
                      Note: stopPropagation so the click doesn't ALSO open the
                      drawer — the drawer already has an Impersonate button. */}
                  <a
                    href={`/t/${t.subdomain}/login`}
                    target="_blank" rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 text-[#00a69c] hover:underline text-xs font-medium"
                    data-testid={`tenant-open-${t.subdomain}`}
                  >
                    Open <ArrowSquareOut size={12} />
                  </a>
                  <CaretRight size={14} className="inline ml-3 text-[#D5E8E4]" />
                </td>
              </tr>
            ))}
            {tenants.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-10 text-center text-[#6D8E87]">No tenants yet. Create your first one.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create tenant modal */}
      <Dialog open={showCreate} onOpenChange={(v) => !v && !creating && setShowCreate(false)}>
        <DialogContent className="sm:max-w-[560px]" data-testid="create-tenant-modal">
          <DialogHeader>
            <DialogTitle>Onboard a new tenant</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Subdomain *</label>
                <input type="text" value={form.subdomain}
                  onChange={(e) => setForm({ ...form, subdomain: e.target.value })}
                  placeholder="nilkamal"
                  className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm"
                  data-testid="tenant-subdomain-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Company name *</label>
                <input type="text" value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  placeholder="Nilkamal Limited"
                  className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm"
                  data-testid="tenant-companyname-input" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Plan</label>
              <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}
                className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm bg-white"
                data-testid="tenant-plan-select">
                {availablePlans.length === 0 && <option value="">Loading plans…</option>}
                {availablePlans.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} — ${p.monthly_price}/user/mo</option>
                ))}
              </select>
              <p className="text-[10px] text-[#6D8E87] mt-1">
                Plans are managed in the <b>Pricing</b> tab. Any tenant can be upgraded/downgraded later from their detail drawer.
              </p>
            </div>

            <div className="pt-3 border-t border-[#D5E8E4]">
              <p className="text-xs font-semibold text-[#3B5A54] mb-2">
                Seed first admin (optional — if skipped, they use /signup on their subdomain)
              </p>
              <div className="grid grid-cols-2 gap-3">
                <input type="email" value={form.admin_email}
                  onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                  placeholder="admin email"
                  className="px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm"
                  data-testid="tenant-admin-email-input" />
                <input type="text" value={form.admin_name}
                  onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
                  placeholder="admin name"
                  className="px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm"
                  data-testid="tenant-admin-name-input" />
              </div>
              <input type="password" value={form.admin_password}
                onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                placeholder="temp password (they should rotate)"
                className="mt-2 w-full px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm"
                data-testid="tenant-admin-password-input" />
            </div>

            <div className="pt-3 border-t border-[#D5E8E4]">
              <label className="flex items-start gap-2 cursor-pointer" data-testid="tenant-copy-catalog-toggle">
                <input type="checkbox" checked={form.copy_catalog}
                  onChange={(e) => setForm({ ...form, copy_catalog: e.target.checked })}
                  className="mt-0.5 accent-[#00a69c]"
                  data-testid="tenant-copy-catalog-checkbox" />
                <div className="text-sm">
                  <div className="font-semibold text-[#052A23]">Seed starter catalog</div>
                  <div className="text-xs text-[#6D8E87] mt-0.5">
                    Copy products from an existing tenant so the new workspace isn&apos;t empty.
                    COGS values are zeroed out — the new tenant will fill their own.
                  </div>
                </div>
              </label>

              {form.copy_catalog && (
                <div className="mt-3 ml-6 space-y-2">
                  <label className="block text-xs font-semibold text-[#3B5A54]">Copy from</label>
                  <select value={form.copy_from} onChange={(e) => setForm({ ...form, copy_from: e.target.value })}
                    className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm bg-white"
                    data-testid="tenant-copy-from-select">
                    {tenants.length === 0 && <option value="">No existing tenants</option>}
                    {tenants.map((t) => (
                      <option key={t.id} value={t.subdomain}>{t.company_name} ({t.subdomain})</option>
                    ))}
                  </select>
                  <div className="text-xs text-[#6D8E87]" data-testid="tenant-copy-preview">
                    {catalogCountLoading ? <span>Counting products…</span>
                      : catalogCount === null ? <span>Select a source tenant to see product count.</span>
                      : catalogCount === 0 ? <span className="text-amber-600">This tenant has 0 products — nothing to copy.</span>
                      : <span className="text-[#00a69c] font-medium">
                          {catalogCount} product{catalogCount === 1 ? '' : 's'} will be copied (COGS zeroed).
                        </span>}
                  </div>
                </div>
              )}
            </div>

            {createError && <div className="text-sm text-red-600" data-testid="tenant-create-error">{createError}</div>}
          </div>
          <DialogFooter className="mt-4">
            <button type="button" onClick={() => setShowCreate(false)} disabled={creating}
              className="px-4 py-2 text-sm text-[#3B5A54] border border-[#D5E8E4] rounded-lg hover:bg-[#F4F9F7]"
              data-testid="tenant-create-cancel">Cancel</button>
            <button type="button" onClick={submitCreate}
              disabled={creating || !form.subdomain.trim() || !form.company_name.trim()}
              className="px-4 py-2 text-sm font-semibold text-white bg-[#00a69c] hover:bg-[#008b83] rounded-lg disabled:bg-[#00a69c]/50"
              data-testid="tenant-create-submit">
              {creating ? 'Creating…' : 'Create tenant'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tenant detail drawer — lazy-loaded per-row on click. Updates the
          tenants-list state on save so we don't need a full refetch. */}
      {drawerSlug && (
        <TenantDetailDrawer
          subdomain={drawerSlug}
          onClose={() => setDrawerSlug(null)}
          onTenantChange={(updated) => setTenants((prev) => prev.map((t) => t.subdomain === updated.subdomain ? { ...t, ...updated } : t))}
        />
      )}
    </div>
  );
};

export default SuperAdmin;
